import {EventEmitter, Injectable} from '@angular/core';
import {Http, Response, Headers, RequestOptions} from '@angular/http';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import {environment} from 'environments/environment';
import {catchError, map, mergeMap, retry, retryWhen, scan, switchMap} from 'rxjs/operators';
import {Router} from '@angular/router';
import {CookieService} from './cookie.service';
import {AuthTokenService} from './auth-token.service';
import 'rxjs/add/operator/switchMap';
import bugsnag from '@bugsnag/js';
import {defer} from 'rxjs/observable/defer';
import {of} from 'rxjs/observable/of';
import {forkJoin} from 'rxjs/observable/forkJoin';

declare const toastr: any;
declare const $: any;

const bugsnagClient = environment.production ? bugsnag('70a6c0a52e6ed5f484bc5f56d79933a8') : null;

@Injectable()
export class ApiClientService {

    /**
     * Emmit logout event
     * optional accepts redirect URL
     */
    public onLogout: EventEmitter<string> = new EventEmitter();

    /**
     * Creates params string from object
     * optionally filtered by array of keys
     * @param params
     * @param allowed
     * @param excludeEmpty
     */
    public static serialize(params, allowed: string[] = [], excludeEmpty = true): string {
        if (!params) {
            return '';
        }

        const filteredParams = ApiClientService.filter(params, allowed, excludeEmpty);

        const paramsArray: string[] = Object.keys(filteredParams).map(keyName => {
            if (Array.isArray(filteredParams[keyName])) {
                return filteredParams[keyName].map((val) => `${keyName}[]=${val}`).join('&');
            }
            return `${keyName}=${filteredParams[keyName]}`;
        });

        return paramsArray.length ? '?' + paramsArray.join('&') : '';
    }

    /**
     * Filter params object
     *
     * @param params
     * @param allowed
     * @param excludeEmpty
     */
    public static filter(params, allowed: string[] = [], excludeEmpty = true) {
        const result = {};

        Object.keys(params)
            .filter(keyName => !allowed.length || allowed.includes(keyName)) // filter not allowed properties
            .filter(keyName => !excludeEmpty || !!params[keyName]) // filter empty properties
            .forEach(keyName => result[keyName] = params[keyName]);

        return result;
    }

    constructor(
        public http: Http,
        public router: Router,
        private cookieService: CookieService,
        public authTokenService: AuthTokenService
    ) {
    }

    /**
     * Returns Observable of RequestOptions
     * with auth token
     */
    private getOptions(): Observable<RequestOptions> {
        return this.authTokenService.getAccessToken().pipe(
            map(accessToken => {
                const headers = new Headers({
                    'Content-Type': 'application/json',
                    'Authorization': accessToken ? `Bearer ${accessToken}` : ''
                });

                return new RequestOptions({headers: headers});
            })
        );
    }

    // Use this method instead of get()
    public getJson(domain: keyof typeof environment | string, url: string) {
        return this.request('get', `${environment[domain]}${url}`).pipe(
            map((response: Response) => response.json())
        );
    }

    // deprecated! TODO: replace with getJson() and remove
    public get(domain: keyof typeof environment | string, url: string) {
        return this.request('get', `${environment[domain]}${url}`);
    }

    public post(domain: string, url: string, body: Object = null): Observable<any> {
        return this.request('post', `${environment[domain]}${url}`, body).pipe(
            map((response: Response) => response.json())
        );
    }

    public patch(domain: string, url: string, body: Object): Observable<any> {
        return this.request('patch', `${environment[domain]}${url}`, body).pipe(
            map((response: Response) => response.json())
        );
    }

    public remove(domain: string, url: string): Observable<any> {
        return this.request('delete', `${environment[domain]}${url}`).pipe(
            map((response: Response) => response.json())
        );
    }

    /**
     * Sends an API request
     * with authentication headers
     *
     * @param method
     * @param params
     */
    private request(method, ...params): Observable<any> {
        return defer(() => this.getOptions()).pipe(
            switchMap(options => this.http[method](...params, options)),
            retryWhen(this.authRetryStrategy.bind(this))
        );
    }

    /**
     * Retry strategy
     * on auth error response
     *
     * @param errors
     */
    private authRetryStrategy(errors: Observable<any>) {
        return errors.pipe(
            scan<any>((isRevoked: boolean, errorResponse: Response) => {
                if (errorResponse.status === 401) {
                    const body = errorResponse.json();
                    if (body.error_description === 'User account is locked.' && body.error === 'access_denied') { // @todo somehow remove this hardcode
                        this.authTokenService.tokenError$.emit(body.error_description);
                        throw errorResponse;
                    }
                    if (!isRevoked) {
                        this.authTokenService.revokeCurrentAccessToken();
                        return true;
                    }

                    this.authTokenService.tokenError$.emit(); // login form redirect trigger
                }

                throw errorResponse;  // finish
            }, false)
        );
    }

    public errorHandler(error, msg?: string) {
        if (environment.production) {
            bugsnagClient.app = {
                releaseStage: environment.production ? 'production' : 'staging'
            };
        }
        if (msg)
            toastr.error(msg);
        console.error(error);

        if (localStorage.getItem('userData') && environment.production) {
            bugsnagClient.user = {
                name: JSON.parse(localStorage.getItem('userData')).fullName,
                email: JSON.parse(localStorage.getItem('userData')).email
            };
        }
        try {
            if (environment.production) {
                bugsnagClient.notify(new Error(`Status: ${error.status} ${error.statusText}. ${error._body}\n`));
            }

            const errorObj = error && error.hasOwnProperty('_body') && error._body ? JSON.parse(error._body) : {};

            if (error.status === 500) {
                toastr.error('Response status 500. <br> Internal Server Error.');
            }

            if (errorObj.fields) {
                for (const field in errorObj.fields) {
                    if (errorObj.fields[field].errors) {
                        const errorDescription = errorObj.fields[field].errors[0].description;
                        $(`input[name="${field}"]`).addClass('ng-invalid ng-touched');
                        toastr.error(`${field}: ${errorDescription}`);
                    }
                }
            }

            if (errorObj.errors) {
                errorObj.errors.forEach(e => {
                    if (e.description)
                        toastr.error(`${e.description}`);
                });
            }
        } catch (e) {
            console.error('Invalid error handling!', e);
        }
    }

    public getInfiniteList(domain: keyof typeof environment | string, url: string, perPage: number = 100): Observable<any> {
        url += (url.includes('?') ? '&' : '?') + `per-page=${perPage}`;

        return this.getJson(domain, `${url}&page=1`)
            .pipe(
                switchMap(result => {
                    const subs$ = [of(result)];
                    const pages = Math.ceil(result.total / perPage);
                    for (let page = 2; page <= pages; page++) {
                        subs$.push(this.getJson(domain, url + `&page=${page}`));
                    }
                    return forkJoin(...subs$);
                }),
                map(res => {
                    let results = [];
                    res.forEach(result => {
                        results = results.concat(result.entities);
                    });
                    return {
                        total: results.length,
                        entities: results
                    };
                })
            );
    }
}



// WEBPACK FOOTER //
// /app/src/app/services/api-client.service.ts